import React, { useState, useRef, useLayoutEffect } from 'react'
import { Box, Typography, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material'
import dayjs from 'dayjs'
import PetAvatar from './PetAvatar'

// Nami lavender theme tokens (from the design handoff)
const CMPET = {
	surface: '#F5F3FB',
	border: '#E7E2F3',
	field: '#FFFFFF',
	text: '#2E2A40',
	muted: '#8B85A4',
	primary: '#7C6BD9',
	primaryDeep: '#5F4EC2'
}
const FONTPET = "'Outfit', sans-serif"

// Care-note fields in operational order (what a groomer must know first comes
// first). The card is a spec sheet: every field always shows, empty ones as an
// em dash, so the field list is identical pet to pet and cards line up.
const NOTE_FIELDS = [
	{ key: 'medical_info', label: 'Medical' },
	{ key: 'grooming_info', label: 'Grooming' },
	{ key: 'personality', label: 'Personality' }
]

const petNotes = (pet) => NOTE_FIELDS.map((f) => ({ ...f, text: String(pet?.[f.key] || '').trim() }))

const noteLabelSx = {
	fontFamily: FONTPET, fontSize: '10.5px', fontWeight: 700,
	textTransform: 'uppercase', letterSpacing: '0.06em', color: CMPET.muted
}

// Full care-note record for one pet — opens over the page so the cards never
// resize (design: "cm-notes-modal").
function PetNotesDialog({ open, pet, onClose, onEdit }) {
	const btnBase = {
		display: 'inline-flex', alignItems: 'center', gap: '7px', borderRadius: '10px',
		p: '9px 16px', fontFamily: FONTPET, fontSize: '13px', fontWeight: 700,
		textTransform: 'none', lineHeight: 1.2
	}
	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth={false}
			PaperProps={{ sx: { width: 480, maxWidth: '92vw', borderRadius: '14px', overflow: 'hidden' } }}
		>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: '13px', p: '17px 18px', borderBottom: `1px solid ${CMPET.border}` }}>
				<PetAvatar petId={pet?.id} size={42} editable={false} />
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0, mr: 'auto' }}>
					<Typography sx={{ fontFamily: FONTPET, fontSize: '16.5px', fontWeight: 800, letterSpacing: '-0.015em', color: CMPET.text }}>
						{pet?.name}
					</Typography>
					<Typography sx={{ fontFamily: FONTPET, fontSize: '12.5px', fontWeight: 600, color: CMPET.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
						{['Care notes', pet?.breed, pet?.species].filter(Boolean).join(' · ')}
					</Typography>
				</Box>
				<IconButton
					onClick={onClose}
					aria-label="Close"
					sx={{ width: 26, height: 26, flex: 'none', border: `1px solid ${CMPET.border}`, borderRadius: '7px', bgcolor: CMPET.field, color: CMPET.muted, '&:hover': { color: CMPET.primaryDeep, borderColor: CMPET.primary } }}
				>
					<svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" xmlns="http://www.w3.org/2000/svg"><path d="M3.5 3.5l9 9M12.5 3.5l-9 9" /></svg>
				</IconButton>
			</Box>
			<Box sx={{ display: 'flex', flexDirection: 'column', gap: '18px', p: '20px 18px', maxHeight: 380, overflowY: 'auto' }}>
				{petNotes(pet).map((n) => (
					<Box key={n.key}>
						<Typography sx={noteLabelSx}>{n.label}</Typography>
						{n.text ? (
							<Typography sx={{ fontFamily: FONTPET, fontSize: '13.5px', lineHeight: 1.6, color: CMPET.text, mt: '4px', whiteSpace: 'pre-wrap', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
								{n.text}
							</Typography>
						) : (
							<Typography sx={{ fontFamily: FONTPET, fontSize: '13.5px', lineHeight: 1.6, fontWeight: 500, color: CMPET.muted, mt: '4px' }}>
								Not on file
							</Typography>
						)}
					</Box>
				))}
			</Box>
			<Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', p: '14px 18px', borderTop: `1px solid ${CMPET.border}`, bgcolor: CMPET.surface }}>
				<Button
					onClick={onClose}
					sx={{ ...btnBase, bgcolor: CMPET.field, border: `1px solid ${CMPET.border}`, color: CMPET.text, '&:hover': { borderColor: CMPET.primary, bgcolor: CMPET.field } }}
				>
					Close
				</Button>
				{onEdit && (
					<Button
						onClick={onEdit}
						sx={{ ...btnBase, bgcolor: CMPET.primary, color: '#FFFFFF', '&:hover': { bgcolor: CMPET.primaryDeep } }}
					>
						<svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg"><path d="M11.5 2.1a1.6 1.6 0 012.3 2.3l-8.2 8.2L2.2 13.8l1.2-3.4 8.1-8.3z" /></svg>
						<span>Edit notes</span>
					</Button>
				)}
			</Box>
		</Dialog>
	)
}

/**
 * Reusable component for displaying pet information
 * @param {Array} pets - Array of pet objects
 * @param {boolean} compact - If true, shows compact view (for tables/lists)
 * @param {boolean} showDetails - If true, shows species and breed details
 * @param {boolean} fullCard - If true, shows full pet card with all details
 * @param {Object} pet - Single pet object (required when fullCard is true)
 * @param {Function} onEdit - Callback for edit button (only for fullCard)
 * @param {Function} onDelete - Callback for delete button (only for fullCard)
 * @param {boolean} isMobile - Whether to show mobile or desktop layout (only for fullCard)
 */
function PetInfoCard({
	pets = [],
	compact = false,
	showDetails = true,
	fullCard = false,
	pet = null,
	onEdit = null,
	onDelete = null,
	isMobile = false
}) {
	const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
	const [notesOpen, setNotesOpen] = useState(false)
	// Desktop card: "See full notes" dialog + whether any 2-line clamp is
	// actually cutting text off. Measured rather than guessed — the affordance
	// only shows when something is hidden, and the card itself never resizes.
	const [fullNotesOpen, setFullNotesOpen] = useState(false)
	const [notesCut, setNotesCut] = useState(false)
	const notesRef = useRef(null)

	useLayoutEffect(() => {
		const el = notesRef.current
		if (!el) return
		const check = () => setNotesCut(
			[...el.querySelectorAll('.pet-note-clamp')].some((p) => p.scrollHeight > p.clientHeight + 1)
		)
		check()
		if (typeof ResizeObserver === 'undefined') return
		const ro = new ResizeObserver(check)
		ro.observe(el)
		return () => ro.disconnect()
	}, [pet])

	// Format pet birthday
	const formatPetBirthday = (birthday) => {
		if (!birthday) return null
		return dayjs(birthday).format('MMMM D, YYYY')
	}

	const handleDeleteConfirm = () => {
		if (onDelete && pet) {
			onDelete(pet)
		}
		setDeleteConfirmOpen(false)
	}

	const handleDeleteCancel = () => {
		setDeleteConfirmOpen(false)
	}

	// Full card view (desktop or mobile)
	if (fullCard && pet) {
		const cardContent = isMobile ? (
			// Mobile full card view — Nami "cmb-pet" card
			(
				<Box sx={{
					bgcolor: CMPET.field,
					border: `1px solid ${CMPET.border}`,
					borderRadius: '16px',
					padding: '14px',
					boxShadow: '0 1px 3px rgba(46, 42, 64, 0.04)'
				}}>
					{/* Identity row */}
					<Box sx={{ display: 'flex', alignItems: 'center', gap: '11px', minWidth: 0 }}>
						<PetAvatar petId={pet.id} size={46} editable={false} />
						<Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '1px' }}>
							<Typography title={pet.name} sx={{ fontFamily: FONTPET, fontSize: '15.5px', fontWeight: 750, letterSpacing: '-0.01em', color: CMPET.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
								{pet.name}
							</Typography>
							<Typography sx={{ fontFamily: FONTPET, fontSize: '12.5px', fontWeight: 600, color: CMPET.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
								{[pet.breed, pet.species].filter(Boolean).join(' · ') || '—'}
							</Typography>
						</Box>
						{onEdit && (
							<IconButton
								onClick={() => onEdit(pet)}
								aria-label={`Edit ${pet.name}`}
								sx={{ width: 34, height: 34, flexShrink: 0, border: `1px solid ${CMPET.border}`, borderRadius: '9px', bgcolor: CMPET.field, color: CMPET.muted, '&:hover': { color: CMPET.primaryDeep, borderColor: CMPET.primary } }}
							>
								<svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg"><path d="M11.5 2.1a1.6 1.6 0 012.3 2.3l-8.2 8.2L2.2 13.8l1.2-3.4 8.1-8.3z" /></svg>
							</IconButton>
						)}
					</Box>

					{/* Key facts */}
					<Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', mt: '12px' }}>
						{[
							{ label: 'Weight', value: pet.weight ? `${pet.weight} lbs` : '—' },
							{ label: 'Gender', value: pet.sex || '—' },
							{ label: 'Date of birth', value: formatPetBirthday(pet.birthday) || '—' }
						].map((k) => (
							<Box key={k.label} sx={{ bgcolor: CMPET.surface, borderRadius: '10px', padding: '8px 10px', minWidth: 0 }}>
								<Typography sx={{ fontFamily: FONTPET, fontSize: '10.5px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: CMPET.muted }}>
									{k.label}
								</Typography>
								<Typography title={k.value} sx={{ fontFamily: FONTPET, fontSize: '13px', fontWeight: 700, color: CMPET.text, mt: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
									{k.value}
								</Typography>
							</Box>
						))}
					</Box>

					{/* Care notes — collapsible. The toggle always shows so every card
					    has the same collapsed height; expanded, all fields appear in
					    the same order with "Not on file" for blanks. */}
					<Button
						onClick={() => setNotesOpen((o) => !o)}
						aria-expanded={notesOpen}
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between',
							width: '100%',
							mt: '10px',
							padding: '10px 12px',
							border: 'none',
							borderRadius: '10px',
							bgcolor: CMPET.surface,
							color: CMPET.primaryDeep,
							textTransform: 'none',
							fontFamily: FONTPET,
							fontWeight: 700,
							fontSize: '13px',
							'&:hover': { bgcolor: '#EDE9FB' }
						}}
					>
						<span>Care notes</span>
						<Box component="span" sx={{ display: 'flex', color: CMPET.muted, transition: 'transform .15s', transform: notesOpen ? 'rotate(180deg)' : 'none' }}>
							<svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg"><path d="M4.5 6.5L8 10l3.5-3.5" /></svg>
						</Box>
					</Button>
					{notesOpen && (
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', mt: '12px' }}>
							{petNotes(pet).map((n) => (
								<Box key={n.key}>
									<Typography sx={noteLabelSx}>{n.label}</Typography>
									{n.text ? (
										<Typography sx={{ fontFamily: FONTPET, fontSize: '13px', lineHeight: 1.5, color: CMPET.text, mt: '3px', whiteSpace: 'pre-wrap', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
											{n.text}
										</Typography>
									) : (
										<Typography sx={{ fontFamily: FONTPET, fontSize: '13px', lineHeight: 1.5, fontWeight: 500, color: CMPET.muted, mt: '3px' }}>
											Not on file
										</Typography>
									)}
								</Box>
							))}
						</Box>
					)}
				</Box>
			)
		) : (
			// Desktop full card view
			(
				// Desktop full card view — Nami "Option B" pet profile card
				<Box sx={{
					position: 'relative',
					bgcolor: CMPET.field,
					border: `1px solid ${CMPET.border}`,
					borderRadius: '14px',
					padding: '18px',
					boxShadow: '0 1px 3px rgba(46, 42, 64, 0.04)',
					display: 'flex',
					flexDirection: 'column',
					gap: '14px',
					minWidth: 0,
					height: '100%'
				}}>
				{onEdit && (
					<IconButton
						onClick={() => onEdit(pet)}
						aria-label="Edit pet"
						sx={{ position: 'absolute', top: 12, right: 12, width: 26, height: 26, border: `1px solid ${CMPET.border}`, borderRadius: '7px', bgcolor: CMPET.field, color: CMPET.muted, '&:hover': { color: CMPET.primaryDeep, borderColor: CMPET.primary } }}
					>
						<svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg">
							<path d="M11.5 2.1a1.6 1.6 0 012.3 2.3l-8.2 8.2L2.2 13.8l1.2-3.4 8.1-8.3z" />
						</svg>
					</IconButton>
				)}

				<Box sx={{ display: 'flex', alignItems: 'center', gap: '13px', paddingRight: '30px' }}>
					<PetAvatar petId={pet.id} size={50} editable={false} />
					<Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
						<Typography title={pet.name} sx={{ fontFamily: FONTPET, fontSize: '18px', fontWeight: 800, letterSpacing: '-0.015em', color: CMPET.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
							{pet.name}
						</Typography>
						<Typography sx={{ fontFamily: FONTPET, fontSize: '13px', fontWeight: 600, color: CMPET.muted }}>
							{pet.species || '\u2014'}
						</Typography>
					</Box>
				</Box>

				<Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', borderTop: `1px solid ${CMPET.border}`, borderBottom: `1px solid ${CMPET.border}` }}>
				{[
					{ label: 'Breed', value: pet.breed || '\u2014' },
					{ label: 'Weight', value: pet.weight ? `${pet.weight} lbs` : '\u2014' },
					{ label: 'Gender', value: pet.sex || '\u2014', sub: true },
					{ label: 'Date of birth', value: formatPetBirthday(pet.birthday) || '\u2014', sub: true }
				].map((k, i) => (
					<Box key={k.label} sx={{ display: 'flex', flexDirection: 'column', gap: '5px', padding: '12px 0', pl: i % 2 === 1 ? '16px' : 0, borderTop: i >= 2 ? `1px solid ${CMPET.surface}` : 'none', borderLeft: i % 2 === 1 ? `1px solid ${CMPET.surface}` : 'none', minWidth: 0 }}>
						<Typography sx={{ fontFamily: FONTPET, fontSize: '10.5px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: CMPET.muted }}>
							{k.label}
						</Typography>
						<Typography title={k.value} sx={{ fontFamily: FONTPET, fontSize: '14px', fontWeight: k.sub ? 600 : 750, color: k.sub ? CMPET.muted : CMPET.text, lineHeight: 1.3, overflowWrap: 'break-word' }}>
							{k.value}
						</Typography>
					</Box>
				))}
				</Box>

				{/* Care notes — every field always shows (em dash when empty) and is
				    clamped to two lines, so the field list is identical pet to pet
				    and cards in a row stay exactly equal height. */}
				<Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
					<Box ref={notesRef} sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: '12px' }}>
						{petNotes(pet).map((n) => (
							<Box key={n.key} sx={{ px: '1px' }}>
								<Typography sx={noteLabelSx}>{n.label}</Typography>
								{n.text ? (
									<Typography
										className="pet-note-clamp"
										sx={{
											fontFamily: FONTPET, fontSize: '13px', lineHeight: 1.5, color: CMPET.text, mt: '3px',
											whiteSpace: 'pre-wrap', overflowWrap: 'break-word', wordBreak: 'break-word',
											display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden'
										}}
									>
										{n.text}
									</Typography>
								) : (
									<Typography title="Not on file" sx={{ fontFamily: FONTPET, fontSize: '13px', lineHeight: 1.5, color: '#BCB5CC', mt: '3px' }}>
										&mdash;
									</Typography>
								)}
							</Box>
						))}
					</Box>
				</Box>

				{/* Foot — reserved even when empty so equal-height cards keep the same
				    inner rhythm; "See full notes" appears only when a clamp cut text. */}
				<Box sx={{ display: 'flex', alignItems: 'center', minHeight: '18px', mt: 'auto', pt: '2px' }}>
					{notesCut && (
						<Box
							component="button"
							type="button"
							onClick={() => setFullNotesOpen(true)}
							sx={{
								border: 'none', background: 'transparent', p: 0, fontFamily: FONTPET,
								fontSize: '12.5px', fontWeight: 700, color: CMPET.primaryDeep,
								whiteSpace: 'nowrap', cursor: 'pointer',
								'&:hover': { textDecoration: 'underline' }
							}}
						>
							See full notes
						</Box>
					)}
				</Box>
				</Box>
			)
		)

		return (
			<>
				{cardContent}

				{/* Full care notes — desktop "See full notes" dialog */}
				<PetNotesDialog
					open={fullNotesOpen}
					pet={pet}
					onClose={() => setFullNotesOpen(false)}
					onEdit={onEdit ? () => { setFullNotesOpen(false); onEdit(pet) } : null}
				/>

				{/* Delete Confirmation Dialog */}
				<Dialog
					open={deleteConfirmOpen}
					onClose={handleDeleteCancel}
					maxWidth="xs"
					fullWidth
				>
					<DialogTitle>Delete Pet</DialogTitle>
					<DialogContent>
						<Typography>
							Are you sure you want to delete "{pet.name}"? This action cannot be undone.
						</Typography>
					</DialogContent>
					<DialogActions>
						<Button onClick={handleDeleteCancel}>
							Cancel
						</Button>
						<Button
							onClick={handleDeleteConfirm}
							color="error"
							variant="contained"
						>
							Delete
						</Button>
					</DialogActions>
				</Dialog>
			</>
		)
	}

	// Compact/List view (existing functionality for tables and lists)
	if (!pets || pets.length === 0) {
		return null
	}

	// Single pet display
	if (pets.length === 1) {
		const singlePet = pets[0]

		if (compact) {
			// Compact view for desktop table
			return (
				<Box sx={{
					maxWidth: '100%',
					overflow: 'auto',
					'&::-webkit-scrollbar': {
						height: '4px'
					},
					'&::-webkit-scrollbar-track': {
						bgcolor: 'transparent'
					},
					'&::-webkit-scrollbar-thumb': {
						borderRadius: '4px'
					}
				}}>
					<Typography sx={{
						fontFamily: 'Inter',
						fontWeight: 400,
						fontSize: '14px',
						lineHeight: '1.21em',
						letterSpacing: '-1.786%',
						color: '#2D3748',
						whiteSpace: 'nowrap'
					}}>
						{singlePet.name}
					</Typography>
				</Box>
			)
		}

		// Full view with details (mobile)
		return (
			<Box sx={{
				display: 'flex',
				flexDirection: 'column',
				gap: '0px'
			}}>
				<Typography sx={{
					fontFamily: 'Inter',
					fontWeight: 600,
					fontSize: '13px',
					lineHeight: '1.5em',
					color: '#2D3748'
				}}>
					{singlePet.name}
				</Typography>
				{showDetails && (
					<Box sx={{
						display: 'flex',
						gap: '4px'
					}}>
						<Typography sx={{
							fontFamily: 'Inter',
							fontWeight: 400,
							fontSize: '13px',
							lineHeight: '1.5em',
							color: '#A0AEC0'
						}}>
							{singlePet.species || 'Pet'}
						</Typography>
						{singlePet.breed && (
							<>
								<Typography sx={{
									fontFamily: 'Inter',
									fontWeight: 400,
									fontSize: '13px',
									lineHeight: '1.5em',
									color: '#A0AEC0'
								}}>
									‧
								</Typography>
								<Typography sx={{
									fontFamily: 'Inter',
									fontWeight: 400,
									fontSize: '13px',
									lineHeight: '1.5em',
									color: '#A0AEC0'
								}}>
									{singlePet.breed}
								</Typography>
							</>
						)}
					</Box>
				)}
			</Box>
		)
	}

	// Multiple pets display
	if (compact) {
		// Compact view for desktop table
		return (
			<Box sx={{
				maxWidth: '100%',
				overflow: 'auto',
				'&::-webkit-scrollbar': {
					height: '4px'
				},
				'&::-webkit-scrollbar-track': {
					bgcolor: 'transparent'
				},
				'&::-webkit-scrollbar-thumb': {
					borderRadius: '4px'
				}
			}}>
				<Typography sx={{
					fontFamily: 'Inter',
					fontWeight: 400,
					fontSize: '14px',
					lineHeight: '1.21em',
					letterSpacing: '-1.786%',
					color: '#2D3748',
					whiteSpace: 'nowrap'
				}}>
					{pets.length} pets
				</Typography>
			</Box>
		)
	}

	// Full view with pet names listed (mobile)
	return (
		<Box sx={{
			display: 'flex',
			flexDirection: 'column',
			gap: '0px'
		}}>
			<Typography sx={{
				fontFamily: 'Inter',
				fontWeight: 600,
				fontSize: '13px',
				lineHeight: '1.5em',
				color: '#2D3748'
			}}>
				{pets.length} pets
			</Typography>
			<Box sx={{
				display: 'flex',
				gap: '4px'
			}}>
				{pets.slice(0, 2).map((p, idx) => (
					<React.Fragment key={p.id}>
						{idx > 0 && (
							<Typography sx={{
								fontFamily: 'Inter',
								fontWeight: 400,
								fontSize: '13px',
								lineHeight: '1.5em',
								color: '#A0AEC0'
							}}>
								‧
							</Typography>
						)}
						<Typography sx={{
							fontFamily: 'Inter',
							fontWeight: 400,
							fontSize: '13px',
							lineHeight: '1.5em',
							color: '#A0AEC0'
						}}>
							{p.name}
						</Typography>
					</React.Fragment>
				))}
			</Box>
		</Box>
	)
}


export default PetInfoCard
